import type { LatLng } from '../../maps/types';
import type { JobType } from '../../types/database';
import type { ScheduleJobInput, ScheduleLegInput, VehicleChainAssignment } from '../types';
import { timeStringToMinutes } from '../../utils/scheduleConflict';

/** Sydney-area fixture locations (EXT.SCHED A/B/C/D golden case). */
export const SCHED_FIXTURE_LOCATIONS = {
  standbyA: { lat: -33.8688, lng: 151.2093 } satisfies LatLng,
  schoolB: { lat: -33.875, lng: 151.205 } satisfies LatLng,
  court: { lat: -33.956, lng: 151.215 } satisfies LatLng,
  schoolC: { lat: -33.87, lng: 151.22 } satisfies LatLng,
  operaHouse: { lat: -33.8568, lng: 151.2153 } satisfies LatLng,
  schoolD: { lat: -33.865, lng: 151.198 } satisfies LatLng,
} as const;

const t = timeStringToMinutes;

function transferLeg(
  jobId: string,
  legId: string,
  label: string,
  pickup: LatLng,
  dropoff: LatLng,
  earliest: string,
  latest?: string,
  serviceMinutes = 0
): ScheduleLegInput {
  return {
    jobId,
    legId,
    legType: 'transfer',
    label,
    pickup,
    dropoff,
    earliestStartMinutes: t(earliest),
    latestStartMinutes: latest ? t(latest) : t(earliest),
    serviceDurationMinutes: serviceMinutes,
  };
}

function standbyBlockLeg(
  jobId: string,
  legId: string,
  label: string,
  location: LatLng,
  start: string,
  end: string
): ScheduleLegInput {
  return {
    jobId,
    legId,
    legType: 'standby_block',
    label,
    pickup: location,
    dropoff: location,
    earliestStartMinutes: t(start),
    latestStartMinutes: t(start),
    serviceDurationMinutes: 0,
    endTimeMinutes: t(end),
  };
}

const L = SCHED_FIXTURE_LOCATIONS;

/** Manual optimal 2-vehicle solution from REQ-20260713 §3.2 */
export const SCHED_FIXTURE_MANUAL_PLAN: VehicleChainAssignment[] = [
  {
    vehicleId: 'vehicle-1',
    legs: [
      transferLeg('job-d-drop', 'd-leg-1', 'D morning drop-off', L.schoolD, L.court, '09:00:00', '09:15:00', 15),
      standbyBlockLeg('job-a', 'a-standby', 'A Stand by', L.standbyA, '10:30:00', '17:00:00'),
    ],
  },
  {
    vehicleId: 'vehicle-2',
    legs: [
      transferLeg('job-b', 'b-leg-1', 'B to court', L.schoolB, L.court, '09:00:00', '09:00:00', 20),
      transferLeg('job-c', 'c-leg-1', 'C to opera', L.schoolC, L.operaHouse, '11:00:00', '11:15:00', 45),
      transferLeg('job-d-pickup', 'd-leg-2', 'D pickup', L.court, L.schoolD, '12:30:00', '12:45:00', 30),
      transferLeg('job-b', 'b-leg-2', 'B return', L.court, L.schoolB, '14:00:00', '14:15:00', 30),
      transferLeg('job-c', 'c-leg-2', 'C return', L.operaHouse, L.schoolC, '16:00:00', '16:15:00', 45),
    ],
  },
];

export const SCHED_FIXTURE_ALL_JOB_IDS = ['job-a', 'job-b', 'job-c', 'job-d-drop', 'job-d-pickup'];

/** Build planning jobs from golden fixture leg groups */
export function buildAbcdPlanningJobs(): ScheduleJobInput[] {
  const manual = SCHED_FIXTURE_MANUAL_PLAN;
  const jobLegs = new Map<string, ScheduleLegInput[]>();
  const jobTitles: Record<string, string> = {
    'job-a': 'Job A — Stand by',
    'job-b': 'Job B — School run',
    'job-c': 'Job C — Opera',
    'job-d-drop': 'Job D — Morning',
    'job-d-pickup': 'Job D — Afternoon',
  };
  const jobTypes: Record<string, JobType> = {
    'job-a': 'charter_standby',
    'job-b': 'school_run',
    'job-c': 'school_run',
    'job-d-drop': 'school_run',
    'job-d-pickup': 'school_run',
  };

  for (const chain of manual) {
    for (const leg of chain.legs) {
      if (!jobLegs.has(leg.jobId)) jobLegs.set(leg.jobId, []);
      jobLegs.get(leg.jobId)!.push(leg);
    }
  }

  return [...jobLegs.entries()].map(([id, legs]) => ({
    id,
    title: jobTitles[id] ?? id,
    jobType: jobTypes[id] ?? 'school_run',
    passengerCount: 30,
    legs,
  }));
}

export const SCHED_FIXTURE_PLANNING_VEHICLES = [
  { id: 'vehicle-1', fleetNumber: 'BUS-01', capacity: 50 },
  { id: 'vehicle-2', fleetNumber: 'BUS-02', capacity: 50 },
  { id: 'vehicle-3', fleetNumber: 'BUS-03', capacity: 50 },
];


/** Infeasible: long transfer overlaps Stand by window on same vehicle */
export const SCHED_FIXTURE_INFEASIBLE_STANDBY: VehicleChainAssignment[] = [
  {
    vehicleId: 'vehicle-1',
    legs: [
      transferLeg('job-b', 'b-leg-1', 'B to court (long service)', L.schoolB, L.court, '09:00:00', '09:00:00', 360),
      standbyBlockLeg('job-a', 'a-standby', 'A Stand by', L.standbyA, '10:30:00', '17:00:00'),
    ],
  },
];

/** Duplicate job across two vehicles */
export const SCHED_FIXTURE_DUPLICATE_JOB: VehicleChainAssignment[] = [
  {
    vehicleId: 'vehicle-1',
    legs: [transferLeg('job-b', 'b-leg-1', 'B to court', L.schoolB, L.court, '09:00:00')],
  },
  {
    vehicleId: 'vehicle-2',
    legs: [transferLeg('job-b', 'b-leg-2', 'B return', L.court, L.schoolB, '14:00:00')],
  },
];
